<template>
  <div
    class="map"
    ref="map"
  >
    <v-row
      class="map__buttons flex-column"
      dense
    >
      <v-col>
        <v-speed-dial
          v-model="fab"
          direction="right"
          class="map__btn"
        >
          <template v-slot:activator>
            <v-btn
              v-model="fab"
              class="btn--green"
              dark
              fab
              x-small
              :title="
            fab
              ? $i18n.t('dashboard.source.measurement.cancel')
              : !measureType
                ? $i18n.t('dashboard.source.measurement.sketchesAndMeasurements')
                : $i18n.t('dashboard.source.measurement.delete')
          "
              @click="clearMeasurement"
            >
              <v-icon v-if="fab">mdi-close</v-icon>
              <v-icon v-else-if="!fab && measureType">mdi-delete</v-icon>
              <v-icon v-else>mdi-ruler</v-icon>
            </v-btn>
          </template>
          <base-map-button
            title="dashboard.source.measurement.linearSketch"
            @click="addMeasurement('LineString')"
            icon="mdi-chart-line-variant"
          />
          <base-map-button
            title="dashboard.source.measurement.areaSketch"
            @click="addMeasurement('Polygon')"
            icon="mdi-ruler-square"
          />
        </v-speed-dial>
      </v-col>
      <v-col>
        <base-map-button
          title="dashboard.map.zoomToStart"
          @click="zoomToStartView"
          icon="mdi-fullscreen"
        />
      </v-col>
      <v-col>
        <base-map-button
          title="dashboard.source.measurement.addParcel"
          @click="startSelecting"
          :icon="selectingParcelsActive ? 'mdi-close' : 'mdi-border-inside'"
        />
      </v-col>
      <v-col v-if="!singleParcel">
        <base-map-button
          title="dashboard.map.uldk"
          @click="activateUldkDialog"
          icon="mdi-shape-square-plus"
        />
      </v-col>
      <v-col v-if="$route.params.id">
        <base-map-button
          title="offer.zoomToOffer"
          @click="zoomToParcel"
          icon="mdi-magnify"
        />
      </v-col>
      <template v-if="hasGeometry">
        <v-col v-if="singleParcel && !isLayerEditing">
          <base-map-button
            title="dashboard.map.startLayerEdit"
            icon="mdi-pencil"
            @click="startParcelEditing"
          />
        </v-col>
        <v-col v-if="isLayerEditing">
          <base-map-button
            title="parcels.confirmParcelEdit"
            icon="mdi-check"
            @click="endParcelEditing('save')"
          />
        </v-col>
        <v-col v-if="isLayerEditing">
          <base-map-button
            title="parcels.cancelParcelEdit"
            icon="mdi-close"
            @click="endParcelEditing('cancel')"
          />
        </v-col>
      </template>
      <template v-else>
        <v-col v-if="!isDrawingActive">
          <base-map-button
            title="parcels.drawParcel"
            icon="mdi-pencil"
            @click="startDrawingPolygon()"
          />
        </v-col>
        <v-col v-if="isDrawingActive">
          <base-map-button
            title="dashboard.map.accept"
            icon="mdi-check"
            @click="acceptDrawedPolygon(false)"
          />
        </v-col>
        <v-col v-if="isDrawingActive">
          <base-map-button
            title="dashboard.map.cancel"
            icon="mdi-close"
            @click="cancelDrawingPolygon()"
          />
        </v-col>
      </template>
      <template v-if="hasFilter">
        <v-col v-if="!isDrawingActive && !isFilter">
          <base-map-button
            title="dashboard.map.filter"
            icon="mdi-filter"
            @click="startDrawingPolygon('filter')"
          />
        </v-col>
        <v-col v-if="isDrawingActive && isFilter">
          <base-map-button
            title="dashboard.map.accept"
            icon="mdi-check"
            @click="acceptDrawedPolygon(true)"
          />
        </v-col>
        <v-col v-if="isDrawingActive && isFilter">
          <base-map-button
            title="dashboard.map.cancel"
            icon="mdi-close"
            @click="cancelDrawingPolygon()"
          />
        </v-col>
      </template>
      <v-col v-if="hasGeometry && singleParcel">
        <base-map-button
          title="dialog.header.removeGeometry"
          icon="mdi-layers-remove"
          @click="$emit('openRemoveGeometryDialog')"
        />
      </v-col>
      <v-col>
        <base-map-button
          title="dashboard.map.legend"
          icon="mdi-map-legend"
          @click="openMapLegend"
        />
      </v-col>
      <!-- <v-col>
        <base-map-button
          title="dashboard.map.print"
          icon="mdi-printer"
          @click="print"
        />
      </v-col> -->
      <v-col>
        <base-map-button title="dashboard.map.print" icon="mdi-printer" @click="isMapPrintingDialogVisible = true" />
      </v-col>

      <slot name="map-buttons"></slot>
    </v-row>
    <map-address-search @search="searchAddress" @clear="clearSearchAddress"/>
    <layers-loader :items="layersLoader" />
    <map-zoom-level :zoom-level="mapZoom" />
    <v-row class="map__buttons-right flex-column" dense>
      <v-col v-if="editingLayer">
        <base-map-button
          :title="isUserLayerEditing?'dashboard.map.stopLayerEdit':'dashboard.map.startLayerEdit'"
          :icon="isUserLayerEditing? 'mdi-cancel' : 'mdi-pencil'"
          @click="layerEdit"
        />
      </v-col>
      <v-col v-if="isUserLayerEditing">
        <base-map-button
          :title="$i18n.t('dashboard.map.add' + editLayer.geometry_type)"
          :icon="editingTypeIcon"
          @click="addFeature"
        />
      </v-col>
      <v-col v-if="isUserLayerEditing">
        <base-map-button
          title="dashboard.map.selectFeature"
          icon="mdi-cursor-pointer"
          @click="selectFeature"
        />
      </v-col>
    </v-row>
    <MapPrintingDialog :is-visible.sync="isMapPrintingDialogVisible" :parent-map="map" printed-layer-name="parcels" />
    <UldkParcels
      :parent-map="map"
      :dialog="isUldkActive"
      @cancelUldk="isUldkActive = false"
      @changeBaselayer="changeBaselayer"
      @uldkAdded="addParcel"
      v-if="isUldkActive"
    />
    <MapLegendDialog
      v-if="isLegendLoaded"
      ref="map_legend"
      :dialog="isLegendActive"
      :isDashboard="true"
      @toggleWMS="toggleWMS"
      @toggleUserLayer="toggleUserLayer"
      @hideLegend="isLegendActive = false"
    >
      <template v-slot:buttons>
        <v-card-actions>
          <div class="flex-grow-1" />
          <v-btn
            color="primary"
            text
            @click="isLegendActive = false"
          >{{ $i18n.t('dialog.confirmation.close') }}</v-btn>
        </v-card-actions>
      </template>
    </MapLegendDialog>
    <AddNewFeatureDialog
      :dialog="isAddNewFeatureActive"
      :fields="editLayer.fields || []"
      @success="saveUserFeature"
      @cancel="cancelUserFeature"
    />
    <FeatureDialog
      :dialog="isFeatureActive"
      :fields="editLayer.fields || []"
      :selectedFeatureProperties="selectedUserFeature?selectedUserFeature.get('properties'):{}"
      @delete="deleteFeature"
      @cancel="hideFeature"
    />
    <ConfirmDialog
      attach=".map"
      v-if="uldkDialogLoaded"
      :dialog="uldkDialogVisible"
      @success="confirmedUldkParcel()"
      @cancel="uldkDialogVisible = false"
    >
      <template v-slot:header>
        <h1>{{ $i18n.t(`dialog.header.changeParcelGeom`) }}</h1>
      </template>
      <template v-slot:content>
        <p>{{ $i18n.t(`dialog.content.changeParcelGeom`) }}</p>
      </template>
    </ConfirmDialog>
  </div>
</template>

<script>
import _ from 'lodash';
import Map from 'ol/Map';
import View from 'ol/View';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { GeoJSON, WKT } from 'ol/format';
import { containsExtent } from 'ol/extent';
import Select from 'ol/interaction/Select';
import Modify from 'ol/interaction/Modify';
import Draw from 'ol/interaction/Draw';
import { Style, Stroke, Fill, Circle, Text } from 'ol/style';
import proj4 from 'proj4';
// import {fromLonLat} from 'ol/proj';
import { register } from 'ol/proj/proj4';
import { LineString, Polygon } from 'ol/geom.js';
import { getArea, getLength } from 'ol/sphere.js';
import { unByKey } from 'ol/Observable.js';
import Overlay from 'ol/Overlay.js';
import { defaults as defaultControls, FullScreen, ScaleLine } from 'ol/control.js';
import Point from 'ol/geom/Point';
import UldkParcels from '@/components/parcels/UldkParcels';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS';
import { get as getProjection } from 'ol/proj';
import axios from 'axios';
import TileLayer from 'ol/layer/Tile';
import TileGrid from 'ol/tilegrid/TileGrid';
import { getWidth } from 'ol/extent';
import TileWMS from 'ol/source/TileWMS';
import { basemaps } from '@/models/Basemaps';
import { transform } from 'ol/proj';

import pointsWithinPolygon from '@turf/points-within-polygon';
import explode from '@turf/explode';
import centroid from '@turf/centroid';
import booleanWithin from '@turf/boolean-within';
import pointOnFeature from '@turf/point-on-feature';

// import MapPrinting from '@/components/MapPrinting';

import { getWMSLayers } from '@/utils/mapUtils';
import searchAddressMixin from '@/mixins/searchAddressMixin';
import layersLoaderMixin from '@/mixins/layersLoaderMixin';
import userLayersMixin from '@/mixins/userLayersMixin';

import vuetify from '@/plugins/vuetify.js';

const vuetifyColors = vuetify.preset.theme.themes.light;

export default {
  mixins: [searchAddressMixin, layersLoaderMixin, userLayersMixin],
  props: {
    parcels: {
      type: Array,
      required: true
    },
    singleParcel: {
      type: Boolean,
      required: false,
      default: false
    },
    hasFilter: {
      type: Boolean,
      default: true
    }
  },
  components: {
    // MapPrinting,
    UldkParcels,
    MapLegendDialog: () => import('../dashboard/MapLegendDialog'),
    ConfirmDialog: () => import('@/components/ConfirmDialog'),
    AddNewFeatureDialog: () => import('@/components/dashboard/AddNewFeatureDialog'),
    FeatureDialog: () => import('@/components/dashboard/FeatureDialog'),
    MapAddressSearch: () => import('@/components/MapAddressSearch'),
    LayersLoader: () => import('@/components/LayersLoader'),
    MapPrintingDialog: () => import('@/components/printing/MapPrintingDialog'),
    MapZoomLevel: () => import('@/components/MapZoomLevel')
  },
  data: () => ({
    isMapPrintingDialogVisible: false,
    isLayerEditing: false,
    editingFeature: undefined,
    editingFeatureCoords: undefined,
    fab: false,
    measureTooltip: undefined,
    measureTooltipElement: undefined,
    measureType: undefined,
    parcelsAllSource: undefined,
    sketch: undefined,
    // isPrintingVisible: false,
    isUldkActive: false,
    selectingParcelsActive: false,
    featuresInExtent: [],
    // modals
    isLegendActive: false,
    isLegendLoaded: false,
    isFilter: false,
    isDrawingActive: false,
    isParcelEditing: false,
    uldkDialogLoaded: false,
    uldkDialogVisible: false,
    map: undefined
  }),
  computed: {
    newlyAddedParcel() {
      return this.$store.getters.getNewlyAddedParcel;
    },
    wmsLayers() {
      return this.$store.getters.getWMSLayers;
    },
    offersStyle() {
      return this.$store.getters.getOffersStyle;
    },
    parcelsStyle() {
      return this.$store.getters.getParcelsStyle;
    },
    currentBasemap() {
      return this.$store.getters.getCurrentBasemap;
    },
    hasGeometry() {
      return this.parcels && this.parcels[0] && this.parcels[0].geometry;
    },
    mapZoom() {
      if (this.map) {
        return this.map.getView().getZoom();
      }
      return 0;
    }
  },
  watch: {
    currentBasemap(nV) {
      this.changeBaselayer(nV);
    },
    parcels(data) {
      this.loadParcels(data);
    },
    wmsLayers(nV) {
      this.addWmsLayers(nV);
    }
  },
  methods: {
    openMapLegend() {
      this.isLegendActive = true;
    },
    activateUldkDialog() {
      this.isUldkActive = true;
    },
    addWmsLayers(layers) {
      for (let layer of layers) {
        let projExtent;
        if (getProjection(layer.wms_srs).getExtent()) {
          projExtent = getProjection(layer.wms_srs).getExtent();
        } else {
          projExtent = getProjection('EPSG:3857').getExtent();
        }
        let startResolution = getWidth(projExtent) / 512;
        let resolutions = new Array(22);
        for (let i = 0, ii = resolutions.length; i < ii; ++i) {
          resolutions[i] = startResolution / Math.pow(2, i);
        }
        const source = new TileWMS({
          url: `${process.env.VUE_APP_DIVI_BASE_URL}/wms_proxy/${layer.wms_url}`,
          projection: layer.wms_srs,
          params: {
            LAYERS: layer.wms_layers,
            TILED: true,
            VERSION: '1.1.1'
          },
          tileGrid: new TileGrid({
            extent: projExtent,
            resolutions: resolutions,
            tileSize: [512, 512]
          })
        });
        this.map.addLayer(
          new TileLayer({
            name: layer.name_pl,
            visible: false,
            source
          })
        );
        this.addLayersLoaderSource(layer.id, { pl: layer.name_pl, en: layer.name_en }, source);
      }
      this.isLegendLoaded = true;
    },
    // print() {
    //   this.isPrintingVisible = true;
    // },
    addMeasurement(type) {
      this.getInteractionByName('selection').setActive(false); //block offers selection
      let drawLayer = this.getLayerByName('measurement');
      let drawInteraction = this.getInteractionByName('drawMeasurement');

      if (drawInteraction && this.measureType != type) {
        this.map.removeInteraction(drawInteraction);
        this.addMeasurementInteraction(drawLayer, type);
      } else {
        this.addMeasurementInteraction(drawLayer, type);
      }

      this.measureType = type;
      this.getInteractionByName('drawMeasurement').setActive(true);
      this.createMeasureTooltip();
    },
    addMeasurementInteraction(layerName, type) {
      let drawInteraction = new Draw({
        source: layerName.getSource(),
        type: type,
        style: new Style({
          fill: new Fill({
            color: `${vuetifyColors.accent}30`,
          }),
          stroke: new Stroke({
            color: 'rgba(0, 0, 0, 0.5)',
            lineDash: [10, 10],
            width: 2
          }),
          image: new Circle({
            radius: 5,
            stroke: new Stroke({
              color: vuetifyColors.accent
            }),
            fill: new Fill({
              color: `${vuetifyColors.accent}50`
            })
          })
        })
      });
      drawInteraction.set('name', 'drawMeasurement');
      this.map.addInteraction(drawInteraction);

      let listener;
      drawInteraction.on('drawstart', evt => {
        this.getLayerByName('measurement')
          .getSource()
          .clear();
        // set sketch
        this.sketch = evt.feature;

        listener = this.sketch.getGeometry().on('change', evt => {
          if (evt.target instanceof Polygon) {
            this.measureTooltipElement.innerHTML = this.formatArea(evt.target);
            this.measureTooltip.setPosition(
              evt.target.getInteriorPoint().getCoordinates()
            );
          } else if (evt.target instanceof LineString) {
            this.measureTooltipElement.innerHTML = this.formatLength(
              evt.target
            );
            this.measureTooltip.setPosition(evt.target.getLastCoordinate());
          }
        });
      });

      drawInteraction.on('drawend', () => {
        this.measureTooltipElement.className = 'tooltip tooltip-static';
        this.measureTooltip.setOffset([0, -7]);
        // unset sketch
        this.sketch = null;
        unByKey(listener);
      });
    },
    // Dodanie narysowanego ficzera do warstwy działek
    addParcel() {
      const parcel = this.getLayerByName('polygonDrawLayer')
        .getSource()
        .getFeatures()[0];
      const { geometry, type } = new GeoJSON().writeFeatureObject(parcel, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857'
      });
      this.openNewParcelView(
        Object.assign(
          {
            geometry
          },
          {
            pow_dzialki: (getArea(parcel.getGeometry()) / 10000).toFixed(2),
            id: 'new'
          }
        )
      );
    },
    selectParcel() {
      if (!this.selectingParcelsActive) {
        this.map.on('click', this.getULDKParcel);
        this.$refs.map.style.cursor = 'crosshair';
        this.selectingParcelsActive = true;
        return;
      }
      this.map.un('click', this.getULDKParcel);
      this.$refs.map.style.cursor = 'default';
      this.selectingParcelsActive = false;
      return;
    },
    // Wyłącz narzędzie rysowania poligonu
    cancelDrawingPolygon() {
      this.getLayerByName('polygonDrawLayer')
        .getSource()
        .clear();
      this.getInteractionByName('polygonInteraction').setActive(false);
      this.getInteractionByName('selection').setActive(true);
      this.isDrawingActive = false;
      this.isFilter = false;
      this.$emit('cancelFiltering');
    },
    clearMeasurement(e) {
      if (!this.fab && this.measureType) {
        e.stopPropagation();
        this.fab = false;
        this.measureType = false;
        this.getInteractionByName('drawMeasurement').setActive(false);
        this.getInteractionByName('selection').setActive(true);
        this.getLayerByName('measurement')
          .getSource()
          .clear();
        this.measureTooltipElement.parentNode.removeChild(
          this.measureTooltipElement
        ); //clear tooltip
      }
    },
    getULDKParcel(e) {
      this.map.un('click', this.getULDKParcel);
      this.$refs.map.style.cursor = 'default';
      this.selectingParcelsActive = false;
      this.$store
        .dispatch(
          'getParcelULDK',
          transform(e.coordinate, 'EPSG:3857', 'EPSG:2180')
        )
        .then(r => {
          if (r.status === 200) {
            if (r.data.slice(0, 2) === '-1') {
              this.$root.$emit(
                'showSnackbar',
                this.$i18n.t('dashboard.map.parcelNotFound')
              );
            } else {
              let [
                wkt,
                voivodeship,
                county,
                commune,
                number,
                region,
                teryt
              ] = r.data.split('|');
              const ewkt = wkt.split(';');
              if (ewkt.length == 2) {
                wkt = ewkt[1];
              } else {
                wkt = ewkt[0].split('\n')[1];
              }
              const { geometry, pow_dzialki } = this.convertWKTtoGeoJSON(wkt);
              const geoJSONFeature = {
                geometry,
                pow_dzialki,
                wojewodztwo: voivodeship.toLocaleLowerCase(),
                gmina: commune,
                powiat: `powiat ${
                  county === commune ? county : county.toLocaleLowerCase()
                }`,
                nr_ew_dzialek: number,
                nr_obre: region,
                teryt: teryt.trim(),
                wsp_geogr: this.getParcelCentroid(geometry).join(', ')
              };

              if (this.singleParcel) {
                this.changeParcelAttributes(geoJSONFeature);
              } else {
                geoJSONFeature.id = 'new';
                this.openNewParcelView(geoJSONFeature);
              }
            }
          } else {
            this.$root.$emit(
              'showSnackbar',
              this.$i18n.t('dashboard.map.parcelError' + r.data)
            );
          }
        });
    },
    convertWKTtoGeoJSON(wkt, epsg = `SRID=2180`) {
      const olFeature = new WKT({}).readFeature(wkt, {
        dataProjection: `EPSG:${epsg.split('=')[1]}`,
        featureProjection: 'EPSG:3857'
      });
      const geoJSONFeature = JSON.parse(
        new GeoJSON().writeFeature(olFeature, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        })
      );
      geoJSONFeature.properties = {};
      // wyliczanie powierzchni znajduje się w ciele funkcji konwertującej format geometrii, w celu uniknięcia tworzenia OpenLayersowego ficzera po raz drugi
      geoJSONFeature.pow_dzialki = (
        getArea(olFeature.getGeometry()) / 10000
      ).toFixed(2);
      return geoJSONFeature;
    },
    changeBaselayer(layerName) {
      for (let el of this.map.getLayers().getArray()) {
        if (el.get('group') == 'baselayers') {
          if (el.get('name') == layerName) {
            el.setVisible(true);
          } else {
            el.setVisible(false);
          }
        }
      }
    },
    createMeasureTooltip() {
      this.measureTooltipElement = document.createElement('div');
      this.measureTooltipElement.className = 'tooltip tooltip-measure';
      this.measureTooltip = new Overlay({
        element: this.measureTooltipElement,
        offset: [0, -15],
        positioning: 'bottom-center'
      });
      this.map.addOverlay(this.measureTooltip);
    },
    endParcelEditing(editingType) {
      this.editingFeature.set('isEditing', false);
      this.getInteractionByName('modifyInteraction').setActive(false);
      if (editingType === 'cancel') {
        this.editingFeature
          .getGeometry()
          .setCoordinates(this.editingFeatureCoords);
        this.isLayerEditing = false;
      } else {
        const editedFeature = this.getInteractionByName('selection')
          .getFeatures()
          .getArray()[0];

        const transformedParcel = JSON.parse(
          new GeoJSON().writeFeature(editedFeature, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857'
          })
        );
        const area = Number(
          (getArea(editedFeature.getGeometry()) / 10000).toFixed(2)
        );

        const changedProperties = [
          {
            name: 'pow_dzialki',
            value: Number(area)
          }
        ];
        this.$emit('updateGeometry', transformedParcel.geometry);
        this.$emit('updateProperties', changedProperties);
        this.isLayerEditing = false;
      }
    },
    clearSelection() {
      this.getInteractionByName('selection')
        .getFeatures()
        .clear();
    },
    formatArea(polygon) {
      var area = getArea(polygon);
      var output;
      if (area > 10000) {
        output = Math.round((area / 10000) * 100) / 100 + ' ' + 'ha';
      } else {
        output = Math.round((area / 10000) * 1000) / 1000 + ' ' + 'ha';
      }
      return output;
    },
    formatLength(line) {
      var length = getLength(line);
      var output;
      if (length > 100) {
        output = Math.round((length / 1000) * 100) / 100 + ' ' + 'km';
      } else {
        output = Math.round(length * 100) / 100 + ' ' + 'm';
      }
      return output;
    },
    // getPointCoords(feature) {

    // },
    confirmedUldkParcel() {
      this.selectParcel();
      this.uldkDialogVisible = false;
    },
    getInteractionByName(name) {
      return this.map
        .getInteractions()
        .getArray()
        .find(i => i.get('name') === name);
    },
    getLayerByName(name) {
      return this.map
        .getLayers()
        .getArray()
        .find(l => {
          return l.get('name') === name;
        });
    },
    // Włącz narzędzie rysowania poligonu
    startDrawingPolygon(type) {
      this.getInteractionByName('polygonInteraction').setActive(true);
      this.getInteractionByName('selection').setActive(false);
      if (type === 'filter') {
        this.isFilter = true;
      }
      this.isDrawingActive = true;
    },
    loadParcels(data) {
      const features = [];
      for (let parcel of data) {
        if (parcel.geometry) {
          const feature = new GeoJSON().readFeature(parcel.geometry, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857'
          });
          for (const key in parcel) {
            if (key !== 'geometry') {
              feature.set(key, parcel[key]);
            }
          }
          features.push(feature);
        }
      }
      this.getLayerByName('parcels')
        .getSource()
        .clear();
      this.getLayerByName('parcels')
        .getSource()
        .addFeatures(features);
      this.getFeaturesInView();
      if (this.singleParcel && data[0] && data[0].geometry) {
        this.zoomToGeometry(data[0].geometry);
      }
      if (this.newlyAddedParcel && !this.singleParcel && features.length > 0) {
        this.$nextTick(() => {
          this.zoomToParcelById(this.newlyAddedParcel);
          this.$store.commit('setNewlyAddedParcel', null);
        });
      }
    },
    getFeaturesInView() {
      const source = this.getLayerByName('parcels').getSource();
      this.featuresInExtent = [];
      const extent = this.map.getView().calculateExtent();
      source.forEachFeatureInExtent(extent, f => {
        this.featuresInExtent.push(f.get('id'));
      });
      this.$emit('filteredFeaturesWithView', this.featuresInExtent);
    },
    filterWithArea() {
      const points = JSON.parse(
        new GeoJSON().writeFeatures(
          this.getLayerByName('parcels')
            .getSource()
            .getFeatures()
        )
      );
      const filterGeom = this.getLayerByName('polygonDrawLayer')
        .getSource()
        .getFeatures()[0];
      if (!filterGeom) {
        this.$root.$emit(
          'showSnackbar',
          this.$i18n.t(`dashboard.map.noGeometry`)
        );
        return;
      }
      const filterPolygon = JSON.parse(new GeoJSON().writeFeature(filterGeom));

      const filteredParcels = pointsWithinPolygon(
        explode(points),
        filterPolygon
      );

      this.$emit(
        'filteredParcels',
        filteredParcels.features.map(feature => {
          return feature.properties.id;
        })
      );
    },
    initOrtofoto() {
      return new Promise((resolve, reject) => {
        let parser = new WMTSCapabilities();
        axios
          .get(
            'http://mapy.geoportal.gov.pl/wss/service/WMTS/guest/wmts/ORTO?SERVICE=WMTS&REQUEST=GetCapabilities'
          )
          .then(response => {
            return response.data;
          })
          .then(text => {
            var result = parser.read(text);
            var options = optionsFromCapabilities(result, {
              layer: 'ORTOFOTOMAPA',
              matrixSet: 'EPSG:4326'
            });
            resolve(
              new TileLayer({
                opacity: 1,
                visible: false,
                name: 'Ortofotomapa',
                group: 'baselayers',
                source: new WMTS({
                  url:
                    'http://mapy.geoportal.gov.pl/wss/service/WMTS/guest/wmts/ORTO',
                  matrixSet: 'EPSG:4326',
                  format: 'image/png',
                  projection: getProjection('EPSG:4326'),
                  tileGrid: options.tileGrid,
                  style: 'default',
                  wrapX: true
                })
              })
            );
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    removeSelectedParcel() {
      this.selectedFeature.setStyle(null);
    },
    selectParcelById(id) {
      const interaction = this.getInteractionByName('selection');
      if (interaction) interaction.getFeatures().clear();

      if (this.selectedFeature) {
        //remove previous style if exists
        this.removeSelectedParcel();
      }
      if (id) {
        const feature = this.getLayerByName('parcels')
          .getSource()
          .getFeatures()
          .find(f => f.get('id') == id);
        this.selectedFeature = feature;
        if (!this.singleParcel) {
          this.zoomToFeature(feature);
        }
        interaction.getFeatures().push(feature);
        feature.setStyle(
          new Style({
            fill: new Fill({
              color: 'rgba(230, 230, 65, 1)'
            }),
            stroke: new Stroke({
              color: 'rgba(170, 170, 40, 1)',
              width: 4
            }),
            text: new Text({
              text: feature.values_.number,
              font: '15px sans-serif',
              fill: new Fill({ color: 'black' }),
              stroke: new Stroke({
                color: `${vuetifyColors.accent}30`,
                width: 3
              }),
              overflow: this.map.getView().getZoom() > 13 ? true : false,
              offsetX: 0,
              offsetY: -7
            })
          })
        );
      }
    },
    openUldkConfirmationDialog() {
      this.uldkDialogLoaded = true;
      this.uldkDialogVisible = true;
    },
    acceptDrawedPolygon(isFilter) {
      if (isFilter) {
        this.filterWithArea();
      } else {
        this.acceptNewParcel();
      }
    },
    acceptNewParcel() {
      const drawnedPolygon = this.getLayerByName('polygonDrawLayer')
        .getSource()
        .getFeatures()[0];
      const changedProperties = [
        {
          name: 'pow_dzialki',
          value: Number(
            (getArea(drawnedPolygon.getGeometry()) / 10000).toFixed(2)
          )
        }
      ];
      const transformedParcel = JSON.parse(
        new GeoJSON().writeFeature(drawnedPolygon, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        })
      );
      this.$emit('updateGeometry', transformedParcel.geometry);
      this.$emit('updateProperties', changedProperties);
      this.cancelDrawingPolygon();
    },
    toggleWMS(lay) {
      if (this.getLayerByName(lay).getVisible()) {
        this.getLayerByName(lay).setVisible(false);
      } else {
        this.getLayerByName(lay).setVisible(true);
      }
    },
    setViewData() {
      this.$store.commit('setCenter', this.map.getView().getCenter());
      this.$store.commit('setZoom', this.map.getView().getZoom());
    },
    setView(center, zoom, resetCenter) {
      this.map.getView().setCenter(center);
      this.map.getView().setZoom(zoom);
      if (resetCenter) {
        this.$store.commit('setCenter', null);
        this.$store.commit('setZoom', null);
      }
    },
    startParcelEditing() {
      this.isLayerEditing = true;
      this.getInteractionByName('modifyInteraction').setActive(true);
      this.editingFeature = this.getLayerByName('parcels')
        .getSource()
        .getFeatures()
        .find(f => f.get('id') === this.$route.params.id);
      this.editingFeature.set('isEditing', true);
      this.editingFeatureCoords = JSON.parse(
        JSON.stringify(this.editingFeature.getGeometry().getCoordinates())
      );
    },
    getParcelCentroid(parcel) {
      let parcelCentroid = centroid(parcel);
      if (!booleanWithin(parcelCentroid, parcel)) {
        parcelCentroid = pointOnFeature(parcel);
      }
      return parcelCentroid.geometry.coordinates;
    },
    startSelecting() {
      this.singleParcel && this.parcels[0].geometry
        ? this.openUldkConfirmationDialog()
        : this.selectParcel();
    },
    openNewParcelView(parcel) {
      this.$store.commit('setNewParcel', parcel);
      this.$router.push({ name: 'parcel', params: { id: 'new' } });
    },
    changeParcelAttributes(feature) {
      const changedProperties = [
        {
          name: 'pow_dzialki',
          value: Number(feature.pow_dzialki)
        },
        {
          name: 'nr_ew_dzialek',
          value: feature.nr_ew_dzialek
        },
        {
          name: 'nr_obre',
          value: feature.nr_obre
        }
      ];
      this.$emit('updateGeometry', feature.geometry);
      this.$emit('updateProperties', changedProperties);
    },
    zoomToFeature(feature) {
      this.map
        .getView()
        .fit(feature.getGeometry().getExtent(), { duration: 1000 });
    },
    zoomToGeometry(geometry) {
      const feature = new GeoJSON().readFeature(geometry, {
        featureProjection: 'EPSG:3857',
        dataProjection: 'EPSG:4326'
      });
      this.map
        .getView()
        .fit(feature.getGeometry().getExtent(), { duration: 1000 });
    },
    removedGeometry() {
      this.getInteractionByName('selection')
        .getFeatures()
        .clear();
    },
    zoomToExtent(extent) {
      this.map.getView().fit(extent, { maxZoom: 13, duration: 1000 });
    },
    zoomToParcel() {
      this.zoomToExtent(
        this.getLayerByName('parcels')
          .getSource()
          .getExtent()
      );
    },
    zoomToParcelById(oid) {
      const feature = this.getLayerByName('parcels')
        .getSource()
        .getFeatures()
        .find(f => f.get('id') == oid);
      this.zoomToFeature(feature);
    },
    zoomToLastView() {
      this.map.getView().setCenter(this.$store.getters.getParcelMapView.center);
      this.map.getView().setZoom(this.$store.getters.getParcelMapView.zoom);
    },
    zoomToStartView() {
      this.map.getView().setCenter([2229729.4005892696, 6903852.286423635]);
      this.map.getView().setZoom(6);
      this.$emit('clearSelection');
    },
    getWMSLayers
  },
  mounted() {
    this.getWMSLayers().then(r => {
      this.changeBaselayer(this.currentBasemap);
    });
    proj4.defs(
      'EPSG:2180',
      '+proj=tmerc +lat_0=0 +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000 +ellps=GRS80 +units=m +no_defs +axis=enu'
    );
    register(proj4);
    getProjection('EPSG:2180').setExtent([
      144907.1658,
      140544.7241,
      877004.007,
      910679.6817
    ]);
    this.map = window.map = new Map({
      target: this.$el,
      view: new View({
        center: [2229729.4005892696, 6903852.286423635],
        zoom: 6
      }),
      controls: defaultControls().extend([new FullScreen({}), new ScaleLine()])
    });

    //this.initOrtofoto()
    // this.map.addLayer(r);
    for (let basemap of basemaps) {
      this.map.addLayer(basemap);
    }

    // Warstwa działek
    const parcelsLayer = new VectorLayer({
      name: 'parcels',
      source: new VectorSource({}),
      style: feature => {
        let featureStyle;
        if (this.parcelsStyle.type === 'unique') {
          let value = '';
          if (this.parcelsStyle.isArrayValue) {
            const arrayValue =  feature.get(this.parcelsStyle.attribute).map(el => el[this.parcelsStyle.classificationTranslate]);
            value = Array.isArray(arrayValue)
              ? arrayValue.filter(value => value !== null)
                  .sort((a, b) => {
                    return (a || '').toString().toLowerCase() > (b || '').toString().toLowerCase() ? 1 : -1;
                  })
                  .join(', ')
              : arrayValue;
          } else {
            if (this.parcelsStyle.classificationTranslate) {
              value = feature.get(this.parcelsStyle.attribute)[this.parcelsStyle.classificationTranslate];
            } else {
              value = feature.get(this.parcelsStyle.attribute);
            }
          }
          const style = this.parcelsStyle.uniques[value === null ? '' : value];
          featureStyle = new Style({
            fill: new Fill({
              color: style && style.fillColor ? style.fillColor : vuetifyColors.accent
            }),
            stroke: new Stroke({
              color: style && style.strokeColor ? style.strokeColor : 'rgb(0,0,0)',
              width: style && style.strokeWidth ? style.strokeWidth : 2
            })
          });
        } else {
          featureStyle = new Style({
            fill: new Fill({
              color: this.parcelsStyle && this.parcelsStyle.fillColor ? this.parcelsStyle.fillColor : vuetifyColors.accent
            }),
            stroke: new Stroke({
              color: this.parcelsStyle && this.parcelsStyle.strokeColor ? this.parcelsStyle.strokeColor : 'rgb(0,0,0)',
              width: this.parcelsStyle && this.parcelsStyle.strokeWidth ? this.parcelsStyle.strokeWidth : 2
            })
          });
        }
        return featureStyle;
      }
    });
    this.map.addLayer(parcelsLayer);
    let selectInteraction = new Select({
      layers: [this.getLayerByName('parcels')]
    });
    selectInteraction.set('name', 'selection');
    selectInteraction.on('select', e => {
      if (e.deselected.length > 0) {
        this.removeSelectedParcel();
      }
      let id;
      if (e.selected.length > 0) {
        id = e.selected[0].get('id');
        this.selectParcelById(id);
      }
      this.$emit('onSelectedId', id);
    });
    this.map.addInteraction(selectInteraction);
    this.loadParcels(this.parcels);

    let modifyInteraction = new Modify({
      features: selectInteraction.getFeatures()
    });
    modifyInteraction.set('name', 'modifyInteraction');
    modifyInteraction.setActive(false);
    this.map.addInteraction(modifyInteraction);

    // Warstwa filtra #geometrycznego
    let drawLayer = new VectorLayer({
      name: 'polygonDrawLayer',
      source: new VectorSource(),
      style: new Style({
        fill: new Fill({
          color: `${vuetifyColors.accent}30`
        }),
        stroke: new Stroke({
          color: vuetifyColors.accent,
          width: 2
        })
      })
    });
    this.map.addLayer(drawLayer);
    const draw = new Draw({
      source: drawLayer.getSource(),
      type: 'Polygon',
      style: new Style({
        fill: new Fill({
          color: `${vuetifyColors.accent}30`
        }),
        stroke: new Stroke({
          color: vuetifyColors.accent,
          width: 2
        }),
        image: new Circle({
          radius: 5,
          stroke: new Stroke({
            color: vuetifyColors.accent
          }),
          fill: new Fill({
            color: `${vuetifyColors.accent}50`
          })
        })
      })
    });
    draw.set('name', 'polygonInteraction');
    draw.setActive(false);
    this.map.addInteraction(draw);
    draw.on('drawend', () => {
      draw.setActive(false);
    });

    // Warstwa do miarki
    let measurementLayer = new VectorLayer({
      name: 'measurement',
      source: new VectorSource({}),
      style: new Style({
        stroke: new Stroke({
          color: vuetifyColors.accent,
          width: 2
        }),
        fill: new Fill({
          color: `${vuetifyColors.accent}30`
        })
      })
    });
    this.map.addLayer(measurementLayer);
    const debounceFilter = _.debounce(this.getFeaturesInView, 800);
    this.map.on('moveend', () => {
      if (!this.singleParcel) {
        debounceFilter();
        if (!this.$route.params.id) {
          this.$store.commit('setParcelMapView', {
            zoom: this.map.getView().getZoom(),
            center: this.map.getView().getCenter()
          });
        }
      }
    });
    if (this.$store.getters.getParcelMapView) {
      this.zoomToLastView();
    }
  }
};
</script>

<style lang="scss">
.ol-zoom {
  top: 2.5em;
}
.map {
  height: 100%;
  width: 100%;
  .ol-control button {
    background-color: var(--v-primary-base);
  }
  .map__buttons {
    position: absolute;
    z-index: 5;
    left: 12px;
    top: 105px;
    max-height: calc(100% - 80px);
    .v-speed-dial__list {
      z-index: 51;
      padding: 0 2px;
    }
  }
  .map__buttons-right {
    position: absolute;
    z-index: 5;
    right: 12px;
    top: 80px;
    max-height: calc(100% - 80px);
    .v-speed-dial__list {
      z-index: 51;
      padding: 0 2px;
    }
  }
}
.tooltip {
  position: relative;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  color: white;
  padding: 4px 8px;
  opacity: 0.7;
  white-space: nowrap;
}
.tooltip-measure {
  opacity: 1;
  font-weight: bold;
}
.tooltip-static {
  background-color: var(--v-primary-base);
  color: whie;
  border: 1px solid white;
}
.tooltip-measure:before,
.tooltip-static:before {
  border-top: 6px solid rgba(0, 0, 0, 0.5);
  border-right: 6px solid transparent;
  border-left: 6px solid transparent;
  content: '';
  position: absolute;
  bottom: -6px;
  margin-left: -7px;
  left: 50%;
}
.tooltip-static:before {
  border-top-color: var(--v-primary-base);
}
</style>
